import { useEffect, useState } from 'react'

export type ImageStatus = 'idle' | 'loading' | 'loaded' | 'error'

type Tracked = { url: string | null; status: ImageStatus }

/** Tracks whether `url` loads, so callers can tell a failure from loading. */
export function useImageStatus(url: string | null): ImageStatus {
  const [tracked, setTracked] = useState<Tracked>(() => ({
    url,
    status: url ? 'loading' : 'idle',
  }))

  useEffect(() => {
    if (!url) {
      setTracked({ url: null, status: 'idle' })
      return
    }
    setTracked({ url, status: 'loading' })
    let cancelled = false
    const image = new Image()
    image.onload = () => {
      if (!cancelled) setTracked({ url, status: 'loaded' })
    }
    image.onerror = () => {
      if (!cancelled) setTracked({ url, status: 'error' })
    }
    image.src = url
    return () => {
      cancelled = true
    }
  }, [url])

  // When `url` changes, reflect loading/idle synchronously this render — before
  // the effect commits the new status — so the previous url's state (e.g. a
  // stale `error`) is never shown for the new image.
  if (tracked.url !== url) return url ? 'loading' : 'idle'
  return tracked.status
}
